import { useCallback, useState, type CSSProperties, type ReactNode } from 'react';
import { Button } from '../ui/controls';
import { artSvg, ratioOf, STAGE_ART } from './stageArt';

const WELCOMED_KEY = 'artwork.home.welcomed';

function readWelcomed(): boolean {
  try {
    return localStorage.getItem(WELCOMED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Whether this person has moved past the first page, remembered in this browser. */
export function useWelcomed() {
  const [welcomed, setWelcomed] = useState(readWelcomed);
  const welcome = useCallback(() => {
    try {
      localStorage.setItem(WELCOMED_KEY, '1');
    } catch {
      // A private window can refuse storage; the first page then returns next visit.
    }
    setWelcomed(true);
  }, []);
  return { welcomed, welcome };
}

const RISE = 'cubic-bezier(0.2, 0.9, 0.25, 1)';

/**
 * What someone sees on their first visit, and whenever the workspace has no files: a stage
 * that plays once and rests on its last frame, a title, one line, one action. Only
 * transforms and opacity move; with reduced motion it opens on the last frame.
 */
export function FirstVisit({ onNewFile, onShowFiles, busy = false, corner }: { onNewFile: () => void; onShowFiles?: () => void; busy?: boolean; corner?: ReactNode }) {
  return (
    <div className="relative grid h-full place-items-center overflow-hidden px-[24px]">
      <style>{`
        @keyframes home-rise { from { opacity: 0; transform: var(--from); } to { opacity: 1; transform: var(--to); } }
        @keyframes home-fade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .home-anim { animation: none !important; opacity: 1 !important; transform: var(--to, none) !important; } }
      `}</style>
      {corner ? <div className="absolute inset-x-0 top-0 mx-auto flex max-w-[1180px] justify-end px-[16px] pt-[32px] sm:px-[40px]">{corner}</div> : null}
      <div className="flex max-w-[560px] flex-col items-center text-center">
        <div aria-hidden className="relative mb-[36px] h-[240px] w-[min(520px,100vw-48px)]">
          {STAGE_ART.map((art, index) => {
            const x = (index - 2) * 96;
            const turn = (index - 2) * 7;
            const to = `translate(-50%, -50%) translate(${x}px, ${Math.abs(index - 2) * 14}px) rotate(${turn}deg)`;
            return (
              <img
                key={art.id}
                src={artSvg(art)}
                alt=""
                draggable={false}
                className="home-anim absolute left-1/2 top-1/2 max-h-[190px] max-w-[160px] rounded-[4px] shadow-[0_2px_4px_rgb(0_0_0/0.15),0_18px_40px_-12px_rgb(0_0_0/0.55)]"
                style={
                  {
                    aspectRatio: String(ratioOf(art.kind)),
                    zIndex: 10 - Math.abs(index - 2),
                    opacity: 0,
                    '--from': 'translate(-50%, -40%) scale(0.92)',
                    '--to': to,
                    transform: to,
                    animation: `home-rise 700ms ${RISE} ${120 + Math.abs(index - 2) * 110}ms forwards`,
                  } as CSSProperties
                }
              />
            );
          })}
        </div>
        <h1 className="home-anim text-[28px] font-bold tracking-[-0.01em]" style={{ opacity: 0, animation: 'home-fade 500ms ease-out 520ms forwards' }}>
          Artwork
        </h1>
        <p className="home-anim mt-[6px] text-[15px] text-label-2" style={{ opacity: 0, animation: 'home-fade 500ms ease-out 620ms forwards' }}>
          Posters, posts and decks on one endless canvas, with your team.
        </p>
        <div data-tour="artwork.newFile" className="home-anim mt-[22px] flex flex-col items-center gap-[12px]" style={{ opacity: 0, animation: 'home-fade 500ms ease-out 720ms forwards' }}>
          <Button variant="prominent" size="large" icon="plus" onClick={onNewFile} disabled={busy}>
            New File
          </Button>
          {onShowFiles ? (
            <button type="button" onClick={onShowFiles} className="h-[28px] rounded-full px-[10px] text-[13px] text-label-2 transition-colors hover:text-label">
              Show My Files
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
